import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import useLangPref from "@/hooks/useLangPref";

/**
 * Iter-276f — Global language pill.
 *
 * Header pill (persistent across ALL pages) that renders the user's
 * current language as a flag + ISO code. Selection persists via
 * `useLangPref` (localStorage-backed, broadcasts a same-tab event).
 *
 * Per-page behaviour:
 *   - On a listing detail (`/ad/{id}`), the page reads the current
 *     pref on mount and auto-fires `POST /listings/{id}/translate`
 *     to swap title + description in-place. Choosing a new language
 *     here also fires a fresh translate immediately.
 *   - On browse / seller / any other page, choosing a new language
 *     just updates the pref and shows a friendly toast — the choice
 *     applies the next time the user opens a listing.
 *
 * Passing `listingId` puts the pill in "listing detail mode": onTranslate
 * is called with the API response so the parent can swap text; onReset
 * is called when the user picks English. Omit both to get the browse
 * pill behaviour.
 *
 * `zh-TW` is sent to the backend with the exact hyphenated casing.
 */
const LANG_GROUPS = [
  {
    label: "NZ & Pacific",
    items: [
      { code: "en", name: "English", flag: "🇬🇧" },
      { code: "mi", name: "Māori", flag: "🇳🇿" },
      { code: "sm", name: "Samoan", flag: "🇼🇸" },
      { code: "to", name: "Tongan", flag: "🇹🇴" },
      { code: "fj", name: "Fijian", flag: "🇫🇯" },
      { code: "tpi", name: "Tok Pisin", flag: "🇵🇬" },
    ],
  },
  {
    label: "Indian subcontinent",
    items: [
      { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
      { code: "bn", name: "বাংলা", flag: "🇧🇩" },
      { code: "te", name: "తెలుగు", flag: "🇮🇳" },
      { code: "mr", name: "मराठी", flag: "🇮🇳" },
      { code: "ta", name: "தமிழ்", flag: "🇮🇳" },
      { code: "ur", name: "اردو", flag: "🇵🇰" },
      { code: "gu", name: "ગુજરાતી", flag: "🇮🇳" },
      { code: "kn", name: "ಕನ್ನಡ", flag: "🇮🇳" },
      { code: "ml", name: "മലയാളം", flag: "🇮🇳" },
      { code: "pa", name: "ਪੰਜਾਬੀ", flag: "🇮🇳" },
      { code: "or", name: "ଓଡ଼ିଆ", flag: "🇮🇳" },
      { code: "as", name: "অসমীয়া", flag: "🇮🇳" },
    ],
  },
  {
    label: "Global",
    items: [
      { code: "es", name: "Español", flag: "🇪🇸" },
      { code: "zh", name: "简体中文", flag: "🇨🇳" },
      { code: "zh-TW", name: "繁體中文", flag: "🇹🇼" },
      { code: "ar", name: "العربية", flag: "🇸🇦" },
      { code: "pt", name: "Português", flag: "🇵🇹" },
      { code: "fr", name: "Français", flag: "🇫🇷" },
      { code: "de", name: "Deutsch", flag: "🇩🇪" },
      { code: "ja", name: "日本語", flag: "🇯🇵" },
      { code: "ko", name: "한국어", flag: "🇰🇷" },
      { code: "id", name: "Bahasa Indonesia", flag: "🇮🇩" },
      { code: "ru", name: "Русский", flag: "🇷🇺" },
    ],
  },
];

const ALL = LANG_GROUPS.flatMap((g) => g.items);
const BY_CODE = Object.fromEntries(ALL.map((i) => [i.code, i]));

/** Backend-visible translate call. Handles the standard error paths. */
async function fetchTranslation(listingId, code) {
  const { data } = await sharedApi.post(
    `/classifieds/listings/${listingId}/translate`,
    { lang: code },
  );
  return { lang: code, title: data.title, description: data.description, cached: !!data.cached };
}

function toastErr(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 429) toast.error(typeof detail === "string" ? detail : "Too many translation requests — try again shortly.");
  else if (status === 400) toast.error(typeof detail === "string" ? detail : "Unsupported language");
  else if (status === 404) toast.error("Listing not found");
  else toast.error(typeof detail === "string" ? detail : "Translation failed");
}

export default function TranslationSwitcher({ listingId = null, onTranslate, onReset, size = "sm" }) {
  const [lang, setLang] = useLangPref();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const loc = useLocation();

  const active = BY_CODE[lang] || BY_CODE.en;

  const pick = async (code) => {
    setOpen(false);
    if (!code || code === lang) return;
    if (code === "en") {
      setLang("en");
      // On a listing detail, wipe the local translation. On browse
      // pages, no toast — English is the default silent state.
      if (listingId) onReset?.();
      return;
    }
    if (!BY_CODE[code]) return;

    // Listing-detail mode: fire translate immediately and delegate the
    // swap to the parent.
    if (listingId && onTranslate) {
      setPending(true);
      try {
        const t = await fetchTranslation(listingId, code);
        setLang(code);
        onTranslate(t);
      } catch (err) {
        toastErr(err);
      } finally {
        setPending(false);
      }
      return;
    }

    // Browse / seller / anything else — persist the choice + toast
    // that ads will translate when opened. Never over-promises full
    // UI i18n (that isn't shipped yet).
    setLang(code);
    const native = BY_CODE[code].name;
    toast.success(`🌐 Language set to ${native} — ads will show translated on any listing you open.`);
  };

  const btnPad = size === "xs" ? "px-2 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs";

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        data-testid="lang-pill"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change language"
        className={
          "inline-flex items-center gap-1.5 rounded-full bg-white border border-soft hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] text-slate-700 font-semibold transition-colors disabled:opacity-60 " +
          btnPad
        }
      >
        {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span aria-hidden="true">{active.flag}</span>}
        {/* Iter-276f mobile polish: below 380px the header row is tight,
            so collapse to a flag-only chip. The full code + chevron
            return above that breakpoint. `sr-only` keeps the code
            available to screen readers even when visually hidden. */}
        <span className="uppercase tracking-widest hidden min-[380px]:inline">{active.code}</span>
        <span className="sr-only min-[380px]:hidden">{active.code}</span>
        <ChevronDown className="w-3 h-3 opacity-60 hidden min-[380px]:inline-block" />
      </button>
      {open && (
        <>
          <button type="button" aria-hidden="true" onClick={() => setOpen(false)} className="fixed inset-0 z-40 cursor-default" />
          <div
            role="listbox"
            data-testid="clf-txn-menu"
            className="absolute right-0 top-full mt-2 z-50 w-64 max-h-80 overflow-y-auto rounded-2xl bg-white border border-soft shadow-card py-2"
            key={loc.pathname}
          >
            {LANG_GROUPS.map((g) => (
              <div key={g.label}>
                <div className="px-3 pt-2 pb-1 text-[10px] font-bold tracking-widest text-slate-400">{g.label}</div>
                {g.items.map((it) => (
                  <button
                    key={it.code}
                    type="button"
                    role="option"
                    aria-selected={it.code === lang}
                    data-testid={`lang-pill-${it.code}`}
                    onClick={() => pick(it.code)}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-slate-50 ${it.code === lang ? "text-[color:var(--brand-blue)] font-semibold" : "text-slate-700"}`}
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span aria-hidden="true">{it.flag}</span>
                      <span className="truncate">{it.name}</span>
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">{it.code}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Export the API helper + BY_CODE so AdDetailPage can auto-translate on
// mount without duplicating the code list.
export { fetchTranslation, BY_CODE };
